import React from 'react';
import { Backdrop, Box, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Trans } from '@lingui/react/macro';
import CheckIcon from '@watershed/icons/components/Checkmark';
import ChevronDownIcon from '@watershed/icons/components/ChevronDown';
import {
  routeForFinanceHome,
  routeForHome,
  routeForMeasureProjects,
} from '@watershed/shared-universal/dashboardRoutes';
import { format as formatUrl } from 'url';
import Logo from '@watershed/ui-core/components/Logo';
import { getPaletteUtils, mixinSx, sxType } from '@watershed/style/styleUtils';
import type { Dispatch, SetStateAction } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { transition } from './utils';
import { getSharedSidebarStyles } from './variants/sharedSidebarStyles';
import useHasPermission from '../../utils/useHasPermission';
import { PermissionType } from '@watershed/constants/permissions';
import { useUserContext } from '../../utils/UserContext';
import { hasPermissionsForMeasurementProjects } from '../../utils/featureAccess';
import { AnimatedWordmark } from './AnimatedWordmark';
import { useFeatureFlag } from '../../utils/FeatureFlag';
import { Flags } from '@watershed/constants/flags';

function useSx() {
  const theme = useTheme();
  const isLightTheme = useFeatureFlag(Flags.LightThemeSideNavigation);
  const { COLORS, BOX_SHADOW_FOCUS } = getSharedSidebarStyles(
    theme,
    isLightTheme
  );

  const logoMarkSxProps = sxType({
    backgroundColor: COLORS.BACKGROUND,
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    padding: '8px',
    marginLeft: '3px',
    height: '36px',
    textDecoration: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 18,
    transition: transition('opacity'),
    border: '0.5px solid transparent',

    '&, &:focus': {
      color: COLORS.TEXT_PRIMARY,
    },
    '&:focus-visible': {
      boxShadow: BOX_SHADOW_FOCUS,
    },

    '&[aria-current="page"]': {
      borderColor: theme.palette.grey70,
      background: theme.palette.grey80,
      boxShadow: '0px 1px 0.5px 0px rgba(79, 89, 110, 0.08)',
    },
  });

  const popoverLogoMarkSxProps = sxType({
    ...logoMarkSxProps,
    '&:hover': {
      backgroundColor: COLORS.BACKGROUND_ACTIVE,
    },
  });

  return {
    logoMarkSxProps,

    popoverLogoMarkSxProps,

    popoverSxProps: sxType({
      zIndex: 3, // over isSticky NavSection + popover Backdrop
      backgroundColor: theme.palette.grey100,
      borderRadius: 1,
      boxShadow: (theme) => getPaletteUtils(theme.palette).boxShadowMenu,
    }),

    logoMarkTextProps: sxType({
      display: 'inline-flex',
      marginLeft: '4px',
      color: theme.palette.white,
      transition: transition('opacity'),
    }),

    logoColor: COLORS.LOGO_COLOR,
    wordmarkColor: COLORS.WORDMARK_COLOR,
  };
}

const chevronSxProps: SxProps<Theme> = { marginLeft: '4px' };

const checkSxProps: SxProps<Theme> = { marginLeft: 'auto' };

/**
 * Renders a product name (wordmark/logo/otherwise) that fades in
 * and out when the appearsCollapsed variable changes.
 */
const FadingProductName = memo(function FadingProductName({
  appearsCollapsed,
  children,
}: {
  appearsCollapsed: boolean;
  children: React.ReactNode;
}) {
  const sx = useSx();
  return (
    <Box
      component="span"
      sx={mixinSx(sx.logoMarkTextProps, {
        opacity: appearsCollapsed ? 0 : 1,
      })}
    >
      {children}
    </Box>
  );
});

/**
 * Shows content for one of the products.
 */
function Content({
  showCheck,
  showChevron,
  appearsCollapsed,
  setPopoverOpen,
  shouldTargetFinancePage,
}: {
  showCheck?: boolean;
  showChevron?: boolean;
  appearsCollapsed: boolean;
  popoverOpen: boolean;
  setPopoverOpen: Dispatch<SetStateAction<boolean>>;
  shouldTargetFinancePage: boolean;
}) {
  const sx = useSx();
  const hasHome = useHasPermission([PermissionType.ViewFootprintDetail]);
  const userContext = useUserContext();
  const canAccessProjects = hasPermissionsForMeasurementProjects(userContext);

  const route = useMemo(() => {
    if (showChevron) {
      return undefined;
    }
    if (shouldTargetFinancePage) {
      return routeForFinanceHome({});
    }
    if (hasHome) {
      return routeForHome();
    }
    if (canAccessProjects) {
      return routeForMeasureProjects();
    }

    // TODO (bryan): At this point, we don't have a route to go to, so we return
    // undefined. I'd love to see us borrow the logic from our server-side
    // `forceRedirect` function — but since that's not currently available
    // client-side, let's just return `undefined`.
    return undefined;
  }, [canAccessProjects, hasHome, shouldTargetFinancePage, showChevron]);

  const handleClick = useCallback(() => {
    if (showChevron) {
      setPopoverOpen((popoverOpen) => !popoverOpen);
    }
  }, [setPopoverOpen, showChevron]);

  const innerBox = (
    <Box
      onClick={handleClick}
      sx={mixinSx(
        showChevron ? sx.logoMarkSxProps : sx.popoverLogoMarkSxProps,
        {
          borderRadius: showChevron
            ? shouldTargetFinancePage
              ? '0 0 6px 6px'
              : '6px 6px 0 0'
            : 1,
        }
      )}
      aria-current={showCheck ? 'page' : undefined}
    >
      <Logo size={18} color={sx.logoColor} />
      {shouldTargetFinancePage ? (
        <FadingProductName appearsCollapsed={appearsCollapsed}>
          <Box marginLeft={0.25} lineHeight={1}>
            <Trans context="The name of Watershed's Finance product">
              Finance
            </Trans>
          </Box>
        </FadingProductName>
      ) : (
        <AnimatedWordmark
          size={12}
          appearsCollapsed={appearsCollapsed}
          color={sx.wordmarkColor}
        />
      )}

      {showChevron && (
        <ChevronDownIcon sx={chevronSxProps} color={sx.wordmarkColor} />
      )}
      {showCheck && <CheckIcon sx={checkSxProps} />}
    </Box>
  );

  if (!route) return innerBox;

  return <a href={formatUrl(route)}>{innerBox}</a>;
}

const LogoBar = memo(function LogoBar({
  isOnFinancePage,
  canToggle,
  ...props
}: {
  isOnFinancePage: boolean;
  canToggle: boolean;
  appearsCollapsed: boolean;
}) {
  const sx = useSx();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const contentProps = {
    ...props,
    isOnFinancePage,
    setPopoverOpen,
    popoverOpen,
  };

  return (
    <>
      <Content
        {...contentProps}
        showChevron={canToggle}
        shouldTargetFinancePage={isOnFinancePage}
      />
      {popoverOpen && (
        <>
          <Backdrop
            open={popoverOpen}
            onClick={popoverOpen ? () => setPopoverOpen(false) : undefined}
            sx={{ zIndex: 2 }}
          />
          <Box
            position="fixed"
            width={252}
            top={4}
            left={4}
            padding={0.5}
            display="grid"
            gridTemplateColumns="1fr"
            gap={0.5}
            sx={sx.popoverSxProps}
            zIndex={3}
          >
            <Content
              {...contentProps}
              showChevron={false}
              showCheck={true}
              shouldTargetFinancePage={isOnFinancePage}
            />
            <Content
              {...contentProps}
              showChevron={false}
              showCheck={false}
              shouldTargetFinancePage={!isOnFinancePage}
            />
          </Box>
        </>
      )}
    </>
  );
});

export default LogoBar;
